import { useEffect, useRef, useState, type JSX } from 'react';

import { useMutation } from '@apollo/client/react';
import log from 'loglevel';

import { useToast } from '@admin-platform/ui/components';
import X from '@admin-platform/ui/icons/solid/x-mark.icon.svg?react';

import { Button } from '../../../components/Button/Button';
import { Form } from '../../../components/Form/Form';
import { FormGroup } from '../../../components/Form/FormGroup';
import { Label } from '../../../components/Inputs/Label';
import { SingleSelect, type SelectOption } from '../../../components/Inputs/SingleSelect';
import LoadingIndicator from '../../../components/LoadingIndicator/LoadingIndicator';
import {
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalTitle,
} from '../../../components/Modal/Modal';
import { parseGraphQLErrorForUser } from '../../../utils/normalizeGraphqlError';

import type { UpdateTenantUserMutationVariables } from './mutations/updateTenantUser.mutation.graphql';
import UPDATE_TENANT_USER from './mutations/updateTenantUser.mutation.graphql';

import styles from './ChangeRoleModal.module.css';

/**
 * The roles this screen can assign. Kept in step with `InviteUserModal` — and
 * with `TENANT_ROLES` on the API, which rejects anything outside it.
 */
const ASSIGNABLE_ROLES: SelectOption[] = [
  { value: 'admin', label: 'Admin' },
  { value: 'member', label: 'Member' },
];

/**
 * A member may already hold a role this screen doesn't hand out (`owner`), so
 * it is added as an option purely so the select can render their current value
 * — and so keeping it is a no-op rather than a silent demotion.
 */
function roleOptions(currentRole: string): SelectOption[] {
  if (ASSIGNABLE_ROLES.some((option) => option.value === currentRole)) {
    return ASSIGNABLE_ROLES;
  }
  const label = currentRole.charAt(0).toUpperCase() + currentRole.slice(1);
  return [{ value: currentRole, label }, ...ASSIGNABLE_ROLES];
}

interface ChangeRoleModalProps {
  /** The membership being edited; null closes the modal. */
  user: { id: string; role: string; displayName: string } | null;
  onClose: () => void;
  onSuccess: () => void;
}

export function ChangeRoleModal({
  user,
  onClose,
  onSuccess,
}: ChangeRoleModalProps): JSX.Element | null {
  const toast = useToast();
  const formRef = useRef<HTMLFormElement>(null);
  const [role, setRole] = useState(user?.role ?? '');

  // Re-seed when a different member is opened from the row menu.
  useEffect(() => {
    setRole(user?.role ?? '');
  }, [user?.id, user?.role]);

  const [updateTenantUser, { loading }] = useMutation<unknown, UpdateTenantUserMutationVariables>(
    UPDATE_TENANT_USER,
    {
      onCompleted: () => {
        toast('Role updated successfully', 'success');
        onSuccess();
      },
      onError: (error) => {
        log.error('Error updating user role:', error);
        toast(parseGraphQLErrorForUser(error), 'error');
      },
    },
  );

  if (!user) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (role === user.role) return;
    await updateTenantUser({ variables: { input: { id: user.id, role } } });
  };

  return (
    <Modal isOpen onClose={onClose}>
      <ModalHeader>
        <ModalTitle>Change Role</ModalTitle>
        <Button
          variant="outline"
          onClick={onClose}
          forIconOnly
          aria-label="Close change role dialog"
        >
          <X className={styles.CloseIcon} />
        </Button>
      </ModalHeader>
      <ModalBody>
        <Form type="grid" onSubmit={handleSubmit} ref={formRef}>
          <FormGroup>
            <Label htmlFor="role">Role for {user.displayName}</Label>
            <SingleSelect
              id="role"
              options={roleOptions(user.role)}
              value={role}
              onChange={(value) => setRole(value as string)}
              placeholder="Select role"
              disabled={loading}
            />
            <p className={styles.HelpText}>
              Admins have full access, including managing team members. Members have limited access.
            </p>
          </FormGroup>
        </Form>
      </ModalBody>
      <ModalFooter>
        <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          type="button"
          variant="primary"
          disabled={loading || role === user.role}
          onClick={() => formRef.current?.requestSubmit()}
        >
          {loading && <LoadingIndicator />}
          Save Role
        </Button>
      </ModalFooter>
    </Modal>
  );
}
